import { useEffect, useState } from "react";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import WorkLog from "../models/WorkLog";
import AssignationService from "../services/AssignationService";
import PositionService from "../services/PositionService";
import WorkLogMonthlyService from "../services/WorkLogMonthlyService";
import WorkLogWeeklyService from "../services/WorkLogWeeklyService";
import RoleUtils from "../utils/RoleUtils";
import { groupRowsByWeek, weekStartDayOf, WorklogWeek } from "../utils/WorklogWeekUtils";

interface UseWorklogWeekApprovalParams {
  rows: WorkLog[];
  isAssignationActive: (row: WorkLog) => boolean;
  assignationExternalId?: string;
  projectExternalId?: string;
  enabled: boolean;
  refreshWorklogs: () => Promise<void> | void;
}

/** `blocked`: only an admin can reopen. `confirm-open`: an admin must confirm before the report is opened. */
export type ReportLockPrompt = { kind: "blocked" | "confirm-open"; week: WorklogWeek; approve: boolean };

const errorMessageOf = (error: any, fallback: string): string => error?.apiResponse?.message ?? error?.message ?? fallback;

/** Same endpoint as the dashboard (`WorkLogWeeklyService.approveWeek`). */
const useWorklogWeekApproval = ({
  rows,
  isAssignationActive,
  assignationExternalId,
  projectExternalId,
  enabled,
  refreshWorklogs,
}: UseWorklogWeekApprovalParams) => {
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const [busyWeekKey, setBusyWeekKey] = useState<string | undefined>(undefined);
  const [lockPrompt, setLockPrompt] = useState<ReportLockPrompt | undefined>(undefined);
  const [resolvedProjectId, setResolvedProjectId] = useState<string | undefined>(undefined);

  // Keep a just-written approval until the reloaded listing says the same thing. Dropping it on every reload snapped the checkbox back.
  useEffect(() => {
    setOverrides((current) => {
      const next: Record<string, boolean> = {};
      for (const [key, approved] of Object.entries(current)) {
        const [year, month, startDay] = key.split("-").map(Number);
        const weekRows = rows.filter(
          (row) => row.shortDay.getFullYear() === year && row.shortDay.getMonth() === month && weekStartDayOf(row.shortDay) === startDay,
        );
        if (weekRows.length === 0) continue;
        const listedAsApproved = weekRows.some((row) => row.weeklyApproved === true);
        if (listedAsApproved !== approved) next[key] = approved;
      }
      return next;
    });
  }, [rows]);

  const allWeeks = enabled ? groupRowsByWeek(rows, isAssignationActive, overrides) : [];
  const weeks = allWeeks.filter((week) => week.hasActiveDays);

  const isDayApproved = (day: WorkLog): boolean =>
    allWeeks.find((week) => week.startDay === weekStartDayOf(day.shortDay))?.approved ?? day.weeklyApproved === true;

  const ensureProjectId = async (): Promise<string | undefined> => {
    const projectFromRows = rows.find((row) => row.projectExternalId)?.projectExternalId;
    if (projectExternalId) return projectExternalId;
    if (projectFromRows) return projectFromRows;
    if (resolvedProjectId) return resolvedProjectId;
    if (!assignationExternalId) return undefined;

    const assignation = (await AssignationService.getByExternalId(assignationExternalId)).getContentOrThrowError();
    const fromAssignation = assignation?.position?.projectExternalId;
    if (fromAssignation) {
      setResolvedProjectId(fromAssignation);
      return fromAssignation;
    }

    const positionId = assignation?.position?.externalId;
    if (!positionId) return undefined;
    const position = (await PositionService.getByExternalId(positionId)).getContentOrThrowError();
    if (position?.projectExternalId) setResolvedProjectId(position.projectExternalId);
    return position?.projectExternalId;
  };

  const applyApproval = async (week: WorklogWeek, approve: boolean) => {
    try {
      const response = await WorkLogWeeklyService.approveWeek({
        assignationExternalId: assignationExternalId!,
        shortWeekFirstDay: week.firstDay,
        validated: approve,
      });
      if (response.getContentOrThrowError().validated !== approve) {
        NotificationService.error("Cannot update. This report had been locked.");
        return;
      }
      setOverrides((prev) => ({ ...prev, [week.key]: approve }));
      NotificationService.info(approve ? `Week ${week.label} approved` : `Week ${week.label} unapproved, its days can be edited now`);
      await refreshWorklogs();
    } catch (error: any) {
      NotificationService.error(errorMessageOf(error, `Unexpected error ${approve ? "approving" : "unapproving"} the week`));
    }
  };

  const isReportLocked = async (week: WorklogWeek, projectId: string): Promise<boolean> => {
    const response = await WorkLogMonthlyService.getByDateAndProject(projectId, week.year, week.month);
    return response.getContentOrThrowError().locked === true;
  };

  const toggleWeek = async (week: WorklogWeek, approve: boolean) => {
    if (!enabled || busyWeekKey) return;
    if (!assignationExternalId) {
      NotificationService.error("The assignation is still loading, try again in a moment");
      return;
    }

    setBusyWeekKey(week.key);
    try {
      let projectId: string | undefined;
      try {
        projectId = await ensureProjectId();
      } catch (error: any) {
        NotificationService.error(errorMessageOf(error, "Could not find the project of this assignation"));
        return;
      }
      if (!projectId) {
        NotificationService.error("Could not find the project of this assignation");
        return;
      }

      let locked: boolean;
      try {
        locked = await isReportLocked(week, projectId);
      } catch (error: any) {
        NotificationService.error(errorMessageOf(error, "Could not check if the report is closed"));
        return;
      }

      if (locked) {
        setLockPrompt({ kind: RoleUtils.isAdmin() ? "confirm-open" : "blocked", week, approve });
        return;
      }

      await applyApproval(week, approve);
    } finally {
      setBusyWeekKey(undefined);
    }
  };

  const dismissLockPrompt = () => setLockPrompt(undefined);

  const confirmOpenReport = async () => {
    const prompt = lockPrompt;
    setLockPrompt(undefined);
    const projectId = projectExternalId || resolvedProjectId;
    if (!prompt || prompt.kind !== "confirm-open" || !projectId) return;

    setBusyWeekKey(prompt.week.key);
    try {
      // `locked: true` is the current state (the endpoint toggles). Noon on the 15th so the date cannot slip into another month.
      const response = await WorkLogMonthlyService.lockReport(projectId, new Date(prompt.week.year, prompt.week.month, 15, 12), true);
      if (!response.isSuccess() || response.getContentOrThrowError()?.locked !== false) {
        NotificationService.error(response.getMessage() ?? "The report could not be opened");
        return;
      }
      await applyApproval(prompt.week, prompt.approve);
    } catch (error: any) {
      NotificationService.error(errorMessageOf(error, "The report could not be opened"));
    } finally {
      setBusyWeekKey(undefined);
    }
  };

  return { weeks, busyWeekKey, toggleWeek, isDayApproved, lockPrompt, dismissLockPrompt, confirmOpenReport };
};

export default useWorklogWeekApproval;
